import {
  Add,
  AddCircleOutline,
  Delete,
  NotificationsOutlined,
  WarningAmberOutlined,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  MenuItem,
  Select,
  type SelectChangeEvent,
  Switch,
  Typography,
} from '@mui/material'
import {
  type Image,
  MasterFieldTypeEnum,
  type ResponseDatetime,
  ResponseDatetimeSubTypeEnum,
  ResponseTextSubTypeEnum,
  ResponseTypeEnum,
  type TemplateNodeSchema,
  type TemplateQuestion,
} from '@ulysses-inc/harami_api_client'
import React, { useId, useState } from 'react'
import { useDrop } from 'react-dnd'
import { NativeTypes } from 'react-dnd-html5-backend'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { getMBSize } from 'src/exShared/util/fileSize'
import convertDisplayFormulaCharacters from 'src/exShared/util/formula/convertDisplayFormulaCharacters'
import convertDisplayCharacters from 'src/exShared/util/numberRule/convertDisplayCharacters'
import { UUID } from 'src/exShared/util/uuid'
import { useLimitedFeatureAvailability } from 'src/features/featureAvailability/useLimitedFeatureAvailability'
import { SelectLabelMasterOptionMessage } from 'src/features/labelCheck/components/SelectLabelMasterOptionMessage'
import { uploadImagesRequestByFiles } from 'src/state/ducks/images/sagas'
import { showEditDeviateNotify as showEditDeviateNotifyAction } from 'src/state/ducks/templates/editDeviateNotify/actions'
import {
  selectIsGridLayout,
  selectQuestionNameByUuid,
} from 'src/state/ducks/templates/selectors'
import type { RootState } from 'src/state/store'
import { EditProductMasterAutofillSettingCheckBox } from '../editProductMasterAutofillSettingCheckBox/EditProductMasterAutofillSettingCheckBox'
import EditResponseFormulaDrawer from '../editResponseFormula/EditResponseFormulaDrawer'
import EditResponseFormulaRuleDrawer from '../editResponseFormulaRule/EditResponseFormulaRuleDrawer'
import EditResponseLabelCheckDrawerContainer from '../editResponseLabelCheck/EditResponseLabelCheckDrawer'
import EditResponseNumberRuleDrawer from '../editResponseNumberRule/EditResponseNumberRuleDrawer'
import EditResponseNumberRuleEnableProductMasterDrawer from '../editResponseNumberRuleEnableProductMaster/EditResponseNumberRuleEnableProductMasterDrawer'
import { MeasurementSettingsModal } from '../editResponseTimeMeasurementRule/MeasurementSettingsModal'
import { RuleDrawer as TimeMeasurementRuleDrawer } from '../editResponseTimeMeasurementRule/RuleDrawer'
import { EditTraceKeyCheckbox } from '../editTraceKey/EditTraceKeyCheckbox'
import { EmployeeInputMethodSelect } from './components/EmployeeInputMethodSelect/EmployeeInputMethodSelect'
import { EmployeeMultipleSelectionCheckbox } from './components/EmployeeMultipleSelectionCheckbox/EmployeeMultipleSelectionCheckbox'
import { DatetimeRule } from './DatatimeRule'
import {
  Container,
  FormatLabel,
  InformationImagesError,
  InformationImagesText,
  InformationImagesWrapper,
  NoPaddingCheckbox,
  OptionColWrapper,
  OptionInformationImagesWrapper,
  OptionWrapper,
  RuleLabel,
  ScaleInput,
  ScaleLabel,
} from './dumb'
import { useCanTrace } from './hooks/useCanTrace'
import { InformationOptionContainer } from './InformationOption'
import { getNumberRuleDisplayCharacters } from './withProductMaster'

interface Props {
  node: TemplateNodeSchema
  updateTemplateNode: (nodeId: number, changeNode: TemplateNodeSchema) => void
  showEditDeviateComment: (node: TemplateNodeSchema) => void
  isEmployeeCheck: boolean
  // 表形式など、検索キーに非対応の質問種別のときは、強制的に非表示にするためのフラグ
  canTrace?: boolean
  /**
   * テキスト回答のフォーマット（1行/複数行）を選択できるか。未指定なら選択できる。
   * 表の列やグリッドレイアウトなど1行固定の文脈では false を渡す。
   */
  canSelectTextFormat?: boolean
  /** 選択肢質問の情報画像を設定できるか。 */
  canConfigureInformationImages?: boolean
}

interface ImageFileInfo {
  uid: string
  url: string
  name: string
}

const checkAnswerOptionSpecified = (node: TemplateNodeSchema) => {
  if (
    node.question?.responseType !== ResponseTypeEnum.NUMBER &&
    node.question?.responseType !== ResponseTypeEnum.TEXT
  ) {
    return false
  }
  return !!node.question?.answerOption
}

/**
 * 質問単位の各種設定を行うためのUIを提供している神コンポーネント
 *
 * - ひな形編集画面における各質問のブロックのうち、質問名/質問種別などを表示している行を除いた他の部分すべてをこのコンポーネントで取り扱っている
 * - 質問種別によって様々なUIを出し分けている
 * - 確実に分割と整理が必要 🚨
 */
const QuestionOptionBar = (props: Props) => {
  const dispatch = useDispatch()

  const { templateNodes } = useSelector(
    (state: RootState) => state.templatesState.templatePages,
  )

  const getInformationImagesFileList = (): ImageFileInfo[] => {
    const informationImages = props.node.question?.informationImages ?? []
    return informationImages.map((image: Image) => ({
      uid: image.uuid ?? UUID(),
      url: image.url ?? '',
      name: image.name ?? '',
    }))
  }

  const onChangeInformationImages = (
    file: File,
    setError: (error: string) => void,
    setLoading: (isLoading: boolean) => void,
  ) => {
    setError('')
    if (file.size > getMBSize(2)) {
      setError('画像ファイルの最大容量は2MBです')
      return
    }
    setLoading(true)
    uploadImagesRequestByFiles([file]).then((images: Image[]) => {
      setLoading(false)
      props.updateTemplateNode(props.node.id, {
        ...props.node,
        question: {
          ...props.node.question,
          informationImages: [
            ...(props.node.question?.informationImages ?? []),
            ...images,
          ],
        },
      })
    })
  }

  const onRemoveInformationImages = (uuid: string) => {
    props.updateTemplateNode(props.node.id, {
      ...props.node,
      question: {
        ...props.node.question,
        informationImages: (
          props.node.question?.informationImages ?? []
        ).filter(image => image.uuid !== uuid),
      },
    })
  }

  const hasRule = (responseType?: ResponseTypeEnum) =>
    responseType === ResponseTypeEnum.MULTIPLE_CHOICE ||
    responseType === ResponseTypeEnum.NUMBER ||
    responseType === ResponseTypeEnum.FORMULA ||
    responseType === ResponseTypeEnum.DATETIME
  const onChangeNumberRuleScale = (scale: string) => {
    props.updateTemplateNode(props.node.id, {
      ...props.node,
      question: {
        ...props.node.question,
        scale: scale,
      },
    })
  }

  const showEditDeviateNotify = (node: TemplateNodeSchema) => {
    dispatch(showEditDeviateNotifyAction(node))
  }

  const isGridLayout = useSelector(selectIsGridLayout, shallowEqual)
  // テキストのフォーマット（1行/複数行）を選択できるか。未指定なら選択できる
  const canSelectTextFormat = props.canSelectTextFormat ?? true
  const canConfigureInformationImages =
    props.canConfigureInformationImages ?? true

  const [isOpenResponseNumberRuleDrawer, setIsOpenResponseNumberRuleDrawer] =
    useState(false)

  const [isLoadingInformationImages, setIsLoadingInformationImages] =
    useState<boolean>(false)
  const [errorInformationImages, setErrorInformationImages] =
    useState<string>('')
  const [isOpenResponseFormulaDrawer, setIsOpenResponseFormulaDrawer] =
    useState<boolean>(false)
  const [previewImage, setPreviewImage] = useState('')
  const [previewVisible, setPreviewVisible] = useState(false)

  const [isOpenMeasurementSettingsDrawer, setIsOpenMeasurementSettingsDrawer] =
    useState(false)
  const [isOpenTimeMeasurementRuleDrawer, setIsOpenTimeMeasurementRuleDrawer] =
    useState(false)
  const [isOpenLabelCheckSettingsDrawer, setIsOpenLabelCheckSettingsDrawer] =
    useState(false)

  const numberScaleLabelId = useId()
  const formulaScaleLabelId = useId()
  const textFormatLabelId = useId()
  const dateFormatLabelId = useId()

  // アプリ全体が react-dnd の HTML5Backend 配下にあり、ネイティブのファイルドラッグも
  // backend が window レベルで横取りするため、素の onDrop ハンドラは発火しない。
  // react-dnd の drop target (NativeTypes.FILE) として登録し backend 経由で受け取る。
  const [, dropImageRef] = useDrop(
    () => ({
      accept: [NativeTypes.FILE],
      drop: (item: { files: File[] }) => {
        const file = item.files?.[0]
        if (file && ['image/png', 'image/jpeg'].includes(file.type)) {
          onChangeInformationImages(
            file,
            setErrorInformationImages,
            setIsLoadingInformationImages,
          )
        }
      },
    }),
    [onChangeInformationImages],
  )

  const defaultResponseTimeMeasurement = {
    subType: ResponseDatetimeSubTypeEnum.DATETIME,
    startQuestionNodeUUID: '',
    endQuestionNodeUUID: '',
    rule: undefined,
  }
  const responseTimeMeasurement =
    (props.node.question?.responseTimeMeasurements ?? [])[0] ||
    defaultResponseTimeMeasurement
  /** 時間計測の設定ボタンの「〜から」の部分 */
  const tmStartQuestionName =
    useSelector((state: RootState) =>
      selectQuestionNameByUuid(
        state,
        responseTimeMeasurement.startQuestionNodeUUID,
      ),
    ) || '質問名なし'
  /** 時間計測の設定ボタンの「〜まで」の部分 */
  const tmEndQuestionName =
    useSelector((state: RootState) =>
      selectQuestionNameByUuid(
        state,
        responseTimeMeasurement.endQuestionNodeUUID,
      ),
    ) || '質問名なし'

  const { isFeatureAvailable } = useLimitedFeatureAvailability()
  const autoCanTrace = useCanTrace(props.node)
  const canTrace = props.canTrace ?? autoCanTrace

  const productMasterFieldDefs = useSelector(
    (state: RootState) =>
      state.productMasterFieldDefsState.productMasterFieldDefs.filter(
        productMasterFieldDef =>
          productMasterFieldDef.fieldType === MasterFieldTypeEnum.Number,
      ),
    shallowEqual,
  )

  if (props.node?.question === undefined) return null

  const responseNumber = (props.node.question.responseNumbers ?? [])[0]
  const responseFormula = (props.node.question.responseFormulas ?? [])[0]
  const scale = props.node.question?.scale ?? ''

  // 各オプションドロワー/モーダルの保存処理。drawer は question パッチだけ作って渡し、
  // ノードへのマージとstateの更新はここで行う。
  const saveQuestionPatch = (questionPatch: Partial<TemplateQuestion>) =>
    props.updateTemplateNode(props.node.id, {
      ...props.node,
      question: { ...props.node.question, ...questionPatch },
    })

  const handlePreview = (url: string) => {
    setPreviewImage(url)
    setPreviewVisible(true)
  }

  const informationImagesfileList = getInformationImagesFileList()

  const responsetType = props.node.question?.responseType

  // 回答種別が「日時」の場合、ルール設定の下部に注意書きを表示する挙動にするため、
  // Container と OptionWrapper のスタイルを個別に設定している
  const containerSx =
    responsetType !== ResponseTypeEnum.DATETIME
      ? { height: 'auto' }
      : { height: 'auto', alignItems: 'start' }
  const optionWrapperStyle: React.CSSProperties | undefined =
    responsetType !== ResponseTypeEnum.DATETIME
      ? undefined
      : {
          height: '36px',
          lineHeight: '32px',
          marginTop: '4px',
        }

  const isAnswerOptionSpecified = checkAnswerOptionSpecified(props.node)
  const canUseProductMasterAutofillSetting =
    isFeatureAvailable('masters') &&
    !isGridLayout &&
    (props.node.question.responseType === ResponseTypeEnum.NUMBER ||
      props.node.question.responseType === ResponseTypeEnum.TEXT)

  return (
    <>
      <Container sx={containerSx} data-testid="question-option-bar">
        {props.node.question?.responseType !==
          ResponseTypeEnum.TIME_MEASUREMENT && (
          <OptionWrapper style={optionWrapperStyle}>
            <FormControlLabel
              control={
                <Checkbox
                  disabled={props.isEmployeeCheck || isAnswerOptionSpecified}
                  // 回答種別変更時のフィールドの整合性を reducer で担保するように設計し切れる場合、isAnswerOptionSpecified によるチェック有無は不要だが
                  // 現状の reducer や各コンポーネントの実装の信頼性が微妙 & 改修に手間がかかるため、isAnswerOptionSpecified も条件に含めている。
                  // 最終的なデータ整合性はバックエンドで担保されるため、ここでのチェックは見栄えのためだけに存在する
                  checked={
                    !isAnswerOptionSpecified &&
                    props.node.question.isRequired === 1
                  }
                  onClick={() => {
                    const isRequired = props.node.question?.isRequired ?? 0
                    props.updateTemplateNode(props.node.id, {
                      ...props.node,
                      question: {
                        ...props.node.question,
                        isRequired: isRequired === 0 ? 1 : 0,
                      },
                    })
                  }}
                />
              }
              label={<Typography variant="body2">必須</Typography>}
            />
          </OptionWrapper>
        )}
        {!isGridLayout && (
          <OptionWrapper style={optionWrapperStyle}>
            <FormControlLabel
              control={
                <Checkbox
                  disabled={props.isEmployeeCheck || isAnswerOptionSpecified}
                  checked={
                    !isAnswerOptionSpecified &&
                    props.node.question.isTimeDisplayed === 1
                  }
                  onClick={() => {
                    const isTimeDisplayed =
                      props.node.question?.isTimeDisplayed ?? 0
                    props.updateTemplateNode(props.node.id, {
                      ...props.node,
                      question: {
                        ...props.node.question,
                        isTimeDisplayed: isTimeDisplayed === 0 ? 1 : 0,
                      },
                    })
                  }}
                />
              }
              label={
                <Typography variant="body2">時刻を記録に表示する</Typography>
              }
            />
          </OptionWrapper>
        )}
        {canUseProductMasterAutofillSetting && (
          <EditProductMasterAutofillSettingCheckBox
            node={props.node}
            onSave={saveQuestionPatch}
          />
        )}
        {canTrace && (
          <EditTraceKeyCheckbox
            node={props.node}
            optionWrapperStyle={optionWrapperStyle}
          />
        )}
        {hasRule(props.node.question.responseType) &&
          props.node.question.responseType !== ResponseTypeEnum.DATETIME && (
            <>
              <OptionWrapper>
                <OptionColWrapper>
                  <Button
                    variant="text"
                    onClick={() => props.showEditDeviateComment(props.node)}
                    disabled={isAnswerOptionSpecified}
                    startIcon={<WarningAmberOutlined />}
                  >
                    逸脱時警告メッセージ
                  </Button>
                </OptionColWrapper>
              </OptionWrapper>
              <OptionWrapper>
                <OptionColWrapper>
                  <Button
                    variant="text"
                    onClick={() => showEditDeviateNotify(props.node)}
                    disabled={isAnswerOptionSpecified}
                    startIcon={<NotificationsOutlined />}
                  >
                    逸脱時通知先
                  </Button>
                </OptionColWrapper>
              </OptionWrapper>
            </>
          )}
        {props.node.question?.responseType ===
          ResponseTypeEnum.TIME_MEASUREMENT && (
          <>
            <OptionWrapper>
              <OptionColWrapper>
                <Button
                  variant="text"
                  onClick={() => props.showEditDeviateComment(props.node)}
                  startIcon={<WarningAmberOutlined />}
                >
                  逸脱時警告メッセージ
                </Button>
              </OptionColWrapper>
            </OptionWrapper>
            <OptionWrapper>
              <OptionColWrapper>
                <Button
                  variant="text"
                  onClick={() => showEditDeviateNotify(props.node)}
                  startIcon={<NotificationsOutlined />}
                >
                  逸脱時通知先
                </Button>
              </OptionColWrapper>
            </OptionWrapper>
            <OptionWrapper>
              <OptionColWrapper>
                <RuleLabel>ルール設定:</RuleLabel>
                <Button
                  variant="text"
                  startIcon={<AddCircleOutline />}
                  onClick={() => setIsOpenTimeMeasurementRuleDrawer(true)}
                >
                  ルールを追加
                </Button>
              </OptionColWrapper>
              <TimeMeasurementRuleDrawer
                isOpen={isOpenTimeMeasurementRuleDrawer}
                questionNode={props.node}
                rule={props.node.question?.responseTimeMeasurements?.[0]?.rule}
                onClose={() => setIsOpenTimeMeasurementRuleDrawer(false)}
                onSave={saveQuestionPatch}
              />
            </OptionWrapper>
          </>
        )}
        {props.node.question?.responseType ===
          ResponseTypeEnum.MULTIPLE_CHOICE &&
          canConfigureInformationImages &&
          !isGridLayout && (
            <OptionWrapper>
              <OptionColWrapper>
                <FormControlLabel
                  control={
                    <Switch
                      checked={
                        props.node.question.isShowInformationImages === 1
                      }
                      onClick={() => {
                        const isShowInformationImages =
                          props.node?.question?.isShowInformationImages ?? 0
                        const informationImages = isShowInformationImages
                          ? []
                          : props.node?.question?.informationImages
                        props.updateTemplateNode(props.node.id, {
                          ...props.node,
                          question: {
                            ...props.node.question,
                            isShowInformationImages:
                              isShowInformationImages === 0 ? 1 : 0,
                            informationImages,
                          },
                        })
                      }}
                    />
                  }
                  label={
                    <InformationImagesText>
                      画像を表示する
                    </InformationImagesText>
                  }
                />
              </OptionColWrapper>
            </OptionWrapper>
          )}
        {props.node.question.responseType === ResponseTypeEnum.NUMBER && (
          <OptionWrapper>
            <OptionColWrapper>
              <RuleLabel>ルール設定:</RuleLabel>
              {responseNumber !== undefined ? (
                <Button
                  variant="text"
                  onClick={() => setIsOpenResponseNumberRuleDrawer(true)}
                  data-testid="edit-number-rule"
                  disabled={isAnswerOptionSpecified}
                >
                  {getNumberRuleDisplayCharacters(
                    scale,
                    responseNumber,
                    productMasterFieldDefs,
                  ) || 'ルールを追加'}
                </Button>
              ) : (
                <Button
                  variant="text"
                  startIcon={<AddCircleOutline />}
                  onClick={() => setIsOpenResponseNumberRuleDrawer(true)}
                  data-testid="edit-number-rule"
                  disabled={isAnswerOptionSpecified}
                >
                  ルールを追加
                </Button>
              )}
              <ScaleLabel id={numberScaleLabelId}>単位:</ScaleLabel>
              <ScaleInput
                name="scale"
                placeholder="℃"
                value={scale}
                onChange={e => onChangeNumberRuleScale(e.target.value)}
                aria-labelledby={numberScaleLabelId}
              />
            </OptionColWrapper>
            {isFeatureAvailable('masters') && !isGridLayout ? (
              <EditResponseNumberRuleEnableProductMasterDrawer
                isOpen={isOpenResponseNumberRuleDrawer}
                questionNode={props.node}
                response={responseNumber}
                onClose={() => setIsOpenResponseNumberRuleDrawer(false)}
                onSave={saveQuestionPatch}
              />
            ) : (
              <EditResponseNumberRuleDrawer
                isOpen={isOpenResponseNumberRuleDrawer}
                questionNode={props.node}
                response={responseNumber}
                onClose={() => setIsOpenResponseNumberRuleDrawer(false)}
                onSave={saveQuestionPatch}
              />
            )}
          </OptionWrapper>
        )}
        {/* テキストのフォーマット（1行/複数行）を選択できるときだけ出す。
            表の列やグリッドレイアウトなど1行固定の文脈では出さない */}
        {props.node.question.responseType === ResponseTypeEnum.TEXT &&
          canSelectTextFormat && (
            <OptionWrapper>
              <FormatLabel id={textFormatLabelId}>フォーマット:</FormatLabel>
              <Select<number>
                defaultValue={props.node.question?.responseTexts?.[0]?.subType}
                value={props.node.question?.responseTexts?.[0]?.subType}
                disabled={isAnswerOptionSpecified}
                sx={{ width: 120 }}
                onChange={(e: SelectChangeEvent<number>) => {
                  const value = e.target.value as number
                  props.updateTemplateNode(props.node.id, {
                    ...props.node,
                    question: {
                      ...props.node.question,
                      responseTexts: [
                        {
                          templateQuestionId: props.node.question?.id,
                          subType: value,
                        },
                      ],
                    },
                  })
                }}
                labelId={textFormatLabelId}
                size="small"
              >
                <MenuItem
                  value={ResponseTextSubTypeEnum.SINGLE_LINE}
                  data-testid="select-format-item"
                >
                  １行
                </MenuItem>
                <MenuItem
                  value={ResponseTextSubTypeEnum.MULTIPLE_LINE}
                  data-testid="select-format-item"
                >
                  複数行
                </MenuItem>
              </Select>
            </OptionWrapper>
          )}
        {props.node.question.responseType === ResponseTypeEnum.DATETIME && (
          <>
            {/* 高さを36pxに固定するとセレクト(40px)がはみ出し、ルール設定が非表示のとき行の下枠線に接触するため、ここだけ固定を外す */}
            <OptionWrapper style={{ ...optionWrapperStyle, height: 'auto' }}>
              <FormatLabel id={dateFormatLabelId}>フォーマット:</FormatLabel>
              <Select<number>
                defaultValue={
                  props.node.question?.responseDatetimes?.[0]?.subType
                }
                sx={{ width: 80 }}
                onChange={(e: SelectChangeEvent<number>) => {
                  const value = e.target.value as number
                  props.updateTemplateNode(props.node.id, {
                    ...props.node,
                    question: {
                      ...props.node.question,
                      responseDatetimes: [
                        {
                          templateQuestionId: props.node.question?.id,
                          subType: value,
                          rule: undefined,
                          isFixedToCurrentTime: false,
                        },
                      ],
                    },
                  })
                }}
                labelId={dateFormatLabelId}
                size="small"
              >
                <MenuItem
                  value={ResponseDatetimeSubTypeEnum.DATETIME}
                  data-testid="select-format-datetime-item"
                >
                  日時
                </MenuItem>
                <MenuItem
                  value={ResponseDatetimeSubTypeEnum.DATE}
                  data-testid="select-format-datetime-item"
                >
                  日付
                </MenuItem>
                <MenuItem
                  value={ResponseDatetimeSubTypeEnum.TIME}
                  data-testid="select-format-datetime-item"
                >
                  時刻
                </MenuItem>
              </Select>
            </OptionWrapper>
            {/* 時間改ざん防止オプション */}
            {props.node.question?.responseDatetimes?.[0]?.subType !==
              ResponseDatetimeSubTypeEnum.DATE && (
              <div style={{ ...optionWrapperStyle, marginLeft: 24 }}>
                <NoPaddingCheckbox
                  checked={
                    props.node.question?.responseDatetimes?.[0]
                      ?.isFixedToCurrentTime
                  }
                  onChange={e => {
                    const responseDatetime =
                      props.node.question?.responseDatetimes?.[0]
                    if (!responseDatetime) {
                      return
                    }

                    const newResponseDateTime: ResponseDatetime = {
                      ...responseDatetime,
                      isFixedToCurrentTime: e.target.checked,
                      rule: undefined,
                    }

                    props.updateTemplateNode(props.node.id, {
                      ...props.node,
                      question: {
                        ...props.node.question,
                        responseDatetimes: [newResponseDateTime],
                      },
                    })
                  }}
                >
                  <Typography variant="body2">
                    現在時刻のみ記録可能にする
                  </Typography>
                </NoPaddingCheckbox>
              </div>
            )}
            {props.node.question?.responseDatetimes?.[0]?.subType !==
              ResponseDatetimeSubTypeEnum.TIME &&
              // 「現在時刻のみ記録可能」を有効にした場合、ルール設定は意味をなさないので非表示にする
              !props.node.question?.responseDatetimes?.[0]
                ?.isFixedToCurrentTime && (
                <>
                  <DatetimeRule
                    responseDatetimes={
                      props.node.question?.responseDatetimes || []
                    }
                    updateTemplateNode={(
                      newResponseDateTime: ResponseDatetime,
                    ) => {
                      props.updateTemplateNode(props.node.id, {
                        ...props.node,
                        question: {
                          ...props.node.question,
                          responseDatetimes: [newResponseDateTime],
                        },
                      })
                    }}
                  />
                  {props.node.question?.responseDatetimes?.[0]?.rule?.value !==
                    undefined && (
                    <OptionWrapper style={optionWrapperStyle}>
                      <Button
                        variant="text"
                        onClick={() => props.showEditDeviateComment(props.node)}
                        startIcon={<WarningAmberOutlined />}
                      >
                        逸脱時警告メッセージ
                      </Button>
                      <Button
                        variant="text"
                        onClick={() => showEditDeviateNotify(props.node)}
                        startIcon={<NotificationsOutlined />}
                      >
                        逸脱時通知先
                      </Button>
                    </OptionWrapper>
                  )}
                </>
              )}
          </>
        )}
        {props.node.question.responseType === ResponseTypeEnum.LABEL_CHECK && (
          <OptionWrapper>
            <OptionColWrapper>
              <Button
                variant="text"
                onClick={() => setIsOpenLabelCheckSettingsDrawer(true)}
              >
                <SelectLabelMasterOptionMessage
                  labelMasterId={
                    props.node.question.responseLabelChecks?.[0]
                      ?.labelInspectionId
                  }
                />
              </Button>
            </OptionColWrapper>
            <EditResponseLabelCheckDrawerContainer
              node={props.node}
              isOpen={isOpenLabelCheckSettingsDrawer}
              onClose={() => setIsOpenLabelCheckSettingsDrawer(false)}
            />
          </OptionWrapper>
        )}
        {props.node.question.responseType === ResponseTypeEnum.EMPLOYEE && (
          <OptionWrapper>
            <EmployeeInputMethodSelect
              node={props.node}
              updateTemplateNode={props.updateTemplateNode}
            />
            <EmployeeMultipleSelectionCheckbox
              node={props.node}
              isEmployeeCheck={props.isEmployeeCheck}
              updateTemplateNode={props.updateTemplateNode}
            ></EmployeeMultipleSelectionCheckbox>
          </OptionWrapper>
        )}
        {props.node.question.responseType === ResponseTypeEnum.FORMULA && (
          <OptionWrapper>
            <OptionColWrapper>
              <RuleLabel>ルール設定:</RuleLabel>
              {responseFormula !== undefined ? (
                <Button
                  variant="text"
                  onClick={() => setIsOpenResponseNumberRuleDrawer(true)}
                  data-testid="edit-number-rule"
                >
                  {convertDisplayCharacters(scale, responseFormula) ||
                    'ルールを追加'}
                </Button>
              ) : (
                <Button
                  variant="text"
                  startIcon={<AddCircleOutline />}
                  onClick={() => setIsOpenResponseNumberRuleDrawer(true)}
                  data-testid="edit-number-rule"
                >
                  ルールを追加
                </Button>
              )}
              <ScaleLabel id={formulaScaleLabelId}>単位:</ScaleLabel>
              <ScaleInput
                name="scale"
                placeholder="℃"
                value={scale}
                onChange={e => onChangeNumberRuleScale(e.target.value)}
                aria-labelledby={formulaScaleLabelId}
              />
            </OptionColWrapper>
            <EditResponseFormulaRuleDrawer
              isOpen={isOpenResponseNumberRuleDrawer}
              questionNode={props.node}
              response={responseFormula}
              onClose={() => setIsOpenResponseNumberRuleDrawer(false)}
              onSave={saveQuestionPatch}
            />
          </OptionWrapper>
        )}
      </Container>
      {props.node.question?.responseType ===
        ResponseTypeEnum.TIME_MEASUREMENT && (
        <Container sx={{ height: '40px' }}>
          <OptionWrapper>
            <OptionColWrapper>
              {responseTimeMeasurement.startQuestionNodeUUID &&
              responseTimeMeasurement.endQuestionNodeUUID ? (
                <Button
                  variant="text"
                  onClick={() => setIsOpenMeasurementSettingsDrawer(true)}
                >
                  {`${tmStartQuestionName}から ${tmEndQuestionName}まで`}
                </Button>
              ) : (
                <Button
                  variant="text"
                  startIcon={<AddCircleOutline />}
                  onClick={() => setIsOpenMeasurementSettingsDrawer(true)}
                >
                  計測を設定する
                </Button>
              )}
            </OptionColWrapper>
            <MeasurementSettingsModal
              isOpen={isOpenMeasurementSettingsDrawer}
              questionNode={props.node}
              response={responseTimeMeasurement}
              onClose={() => setIsOpenMeasurementSettingsDrawer(false)}
              onSave={saveQuestionPatch}
            />
          </OptionWrapper>
        </Container>
      )}
      {canConfigureInformationImages &&
        props.node.question?.isShowInformationImages === 1 &&
        props.node.question?.responseType ===
          ResponseTypeEnum.MULTIPLE_CHOICE && (
          <Container>
            <OptionInformationImagesWrapper>
              <Box sx={{ position: 'relative' }}>
                <InformationImagesWrapper>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {informationImagesfileList.map(file => (
                      <Box
                        key={file.uid}
                        sx={{
                          position: 'relative',
                          width: 104,
                          height: 104,
                          border: '1px solid rgba(0,0,0,0.12)',
                          borderRadius: 1,
                          overflow: 'hidden',
                        }}
                      >
                        <Box
                          component="button"
                          onClick={() => handlePreview(file.url)}
                          sx={{
                            display: 'block',
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            p: 0,
                            cursor: 'pointer',
                            bgcolor: 'transparent',
                          }}
                        >
                          <img
                            src={file.url}
                            alt={file.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                            }}
                          />
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => onRemoveInformationImages(file.uid)}
                          sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            bgcolor: 'rgba(0,0,0,0.4)',
                            color: 'white',
                            '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                          }}
                          aria-label="画像を削除"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                    {(props.node.question?.informationImages ?? []).length <
                      8 && (
                      <Box
                        component="label"
                        ref={dropImageRef as React.Ref<HTMLLabelElement>}
                        sx={{
                          width: 104,
                          height: 104,
                          border: '1px dashed rgba(0,0,0,0.25)',
                          borderRadius: 1,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: 'primary.main',
                            color: 'primary.main',
                          },
                        }}
                      >
                        <input
                          type="file"
                          accept=".png,.jpg,.jpeg"
                          hidden
                          onChange={e => {
                            const file = e.target.files?.[0]
                            if (file) {
                              onChangeInformationImages(
                                file,
                                setErrorInformationImages,
                                setIsLoadingInformationImages,
                              )
                            }
                            e.target.value = ''
                          }}
                        />
                        <Add />
                        <Typography variant="caption">Upload</Typography>
                      </Box>
                    )}
                  </Box>
                  <InformationImagesError>
                    {errorInformationImages}
                  </InformationImagesError>
                </InformationImagesWrapper>
                {isLoadingInformationImages && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'rgba(255,255,255,0.5)',
                    }}
                  >
                    <CircularProgress size={24} />
                  </Box>
                )}
              </Box>
            </OptionInformationImagesWrapper>
            <Dialog
              open={previewVisible}
              onClose={() => setPreviewVisible(false)}
            >
              <DialogContent>
                <img
                  alt="example"
                  style={{ width: '100%' }}
                  src={previewImage}
                />
              </DialogContent>
            </Dialog>
          </Container>
        )}
      {(props.node.question.responseType === ResponseTypeEnum.INFORMATION ||
        props.node.question.responseType === ResponseTypeEnum.URL) && (
        <InformationOptionContainer
          node={props.node}
        ></InformationOptionContainer>
      )}
      {props.node.question.responseType === ResponseTypeEnum.FORMULA && (
        <Container>
          <OptionWrapper>
            {responseFormula !== undefined &&
            responseFormula.tokens &&
            responseFormula.tokens.length > 0 ? (
              <Button
                variant="text"
                onClick={() => setIsOpenResponseFormulaDrawer(true)}
              >
                {convertDisplayFormulaCharacters(
                  Object.values(templateNodes),
                  responseFormula.tokens,
                ) || '計算式を設定する'}
              </Button>
            ) : (
              <Button
                variant="text"
                startIcon={<AddCircleOutline />}
                onClick={() => setIsOpenResponseFormulaDrawer(true)}
              >
                計算式を設定する
              </Button>
            )}
            {isOpenResponseFormulaDrawer && (
              <EditResponseFormulaDrawer
                nodeId={props.node.id}
                responseFormula={responseFormula}
                onClose={() => setIsOpenResponseFormulaDrawer(false)}
                onSave={saveQuestionPatch}
              />
            )}
          </OptionWrapper>
        </Container>
      )}
    </>
  )
}

export default QuestionOptionBar
